/**
 * Interactive P&ID panel — Section E deliverable.
 *
 * • Renders the sheet SVG directly with pan / zoom.
 * • Every rendered element carries `data-elem-id` / `data-elem-type`
 *   attributes.  Clicking any of them opens a side-panel property
 *   editor.
 * • Tag search highlights matching elements with a yellow glow.
 * • Toolbar buttons export the current sheet as a BOM (.xlsx) or a
 *   DXF (AutoCAD) file.
 * • Saved property overrides are persisted server-side and merged
 *   back into the next render.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, FileSpreadsheet, Search, X, RefreshCw, Save, RotateCcw, Sparkles } from "lucide-react";
import { attachPidInteractive, openPidSearch } from "./pidInteractive";
import PidLayerPanel from "./PidLayerPanel";

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Colour tokens (match the rest of the P&ID surface) ─────── */
const COL = {
   bg:      "#020617",
   surface: "#0f1729",
   border:  "#1e293b",
   text:    "#e2e8f0",
   dim:     "#94a3b8",
   spark:   "#22d3ee",
   glow:    "#facc15",
};
const mono = { fontFamily: "'JetBrains Mono', monospace" };

/* ── The main component ────────────────────────────────────── */
export default function InteractivePidPanel({ projectId, sheetNumber,
                                                       initialSvg = "", onClose }) {
   const [svg, setSvg] = useState(initialSvg);
   const [loading, setLoading] = useState(!initialSvg);
   const [selected, setSelected] = useState(null);   // { elem_id, kind, ... }
   const [saving, setSaving] = useState(false);
   const [override, setOverride] = useState({});
   const [searchQuery, setSearchQuery] = useState("");
   const [searchMatches, setSearchMatches] = useState([]);
   const [busyExport, setBusyExport] = useState(null);
   const [aiReview, setAiReview] = useState(null);        // DOC 17 review result
   const [aiBusy, setAiBusy] = useState(false);
   const [showLayerPanel, setShowLayerPanel] = useState(true);
   const [aiPrompt, setAiPrompt] = useState("");
   const [aiFixBusy, setAiFixBusy] = useState(false);
   const [aiFixResult, setAiFixResult] = useState(null);
   const svgHostRef = useRef(null);

   /* Fetch SVG on mount. */
   useEffect(() => {
      if (!projectId || !sheetNumber) return;
      if (initialSvg) return;
      setLoading(true);
      fetch(`${API}/api/projects/${projectId}/pid/generate/${sheetNumber}`, {
         method: "POST",
         credentials: "include",
         headers: { "Content-Type": "application/json" },
         body: "{}",
      })
         .then((r) => r.json())
         .then((d) => setSvg(d.svg || d.svg_content || ""))
         .catch((e) => console.error("SVG fetch failed:", e))
         .finally(() => setLoading(false));
   }, [projectId, sheetNumber, initialSvg]);

   /* Attach click handlers when the SVG changes. */
   useEffect(() => {
      const host = svgHostRef.current;
      if (!host) return;
      const nodes = host.querySelectorAll("[data-elem-id]");
      const handler = (evt) => {
         evt.stopPropagation();
         const g = evt.currentTarget;
         const id = g.getAttribute("data-elem-id");
         const kind = g.getAttribute("data-elem-type");
         if (!id) return;
         loadElement(id, kind);
      };
      nodes.forEach((n) => {
         n.style.cursor = "pointer";
         n.addEventListener("click", handler);
      });
      /* DOC 8 §2/§3/§4 — attach hover tooltip, tag search (Ctrl+F),
       * and OPC click navigation over the same SVG host. */
      const detach = attachPidInteractive(host, {
         onOpcNavigate: (info) => {
            /* Fire a bubbling DOM event that parent viewers can listen
             * for to switch sheets — the InteractivePidPanel only
             * shows one sheet at a time, so we surface it via a
             * custom event instead of an internal state update. */
            host.dispatchEvent(new CustomEvent("pid-opc-navigate", {
               detail: info, bubbles: true,
            }));
         },
         onSearchResult: (hit) => {
            if (hit && hit.tag) loadElement(hit.tag,
                                                        hit.category || "equipment");
         },
      });
      return () => {
         nodes.forEach((n) => n.removeEventListener("click", handler));
         detach && detach();
      };
   }, [svg]);

   /* Load an element from the backend. */
   const loadElement = useCallback(async (elem_id, kind) => {
      try {
         const r = await fetch(
            `${API}/api/projects/${projectId}/drawing/${sheetNumber}/element/${encodeURIComponent(elem_id)}`,
            { credentials: "include" });
         if (!r.ok) throw new Error(await r.text());
         const data = await r.json();
         setSelected(data);
         setOverride(data.override || {});
      } catch (e) {
         console.error("loadElement failed:", e);
         alert("Could not load element: " + e.message);
      }
   }, [projectId, sheetNumber]);

   /* Save override. */
   const saveOverride = useCallback(async () => {
      if (!selected) return;
      setSaving(true);
      try {
         const r = await fetch(
            `${API}/api/projects/${projectId}/drawing/${sheetNumber}/element/${encodeURIComponent(selected.tag || selected.line_number || selected.elem_id)}`,
            { method: "PATCH", credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ override }) });
         if (!r.ok) throw new Error(await r.text());
         alert("Override saved.  Re-generate the sheet to see it applied.");
      } catch (e) {
         alert("Save failed: " + e.message);
      } finally { setSaving(false); }
   }, [selected, override, projectId, sheetNumber]);

   /* Clear override. */
   const clearOverride = useCallback(async () => {
      if (!selected) return;
      if (!confirm("Reset this element to the enricher default?")) return;
      setSaving(true);
      try {
         const r = await fetch(
            `${API}/api/projects/${projectId}/drawing/${sheetNumber}/element/${encodeURIComponent(selected.tag || selected.line_number || selected.elem_id)}`,
            { method: "DELETE", credentials: "include" });
         if (!r.ok) throw new Error(await r.text());
         setOverride({});
         alert("Override cleared.");
      } catch (e) {
         alert("Clear failed: " + e.message);
      } finally { setSaving(false); }
   }, [selected, projectId, sheetNumber]);

   /* Re-generate sheet (fetch new SVG with overrides applied). */
   const regenerate = useCallback(async () => {
      setLoading(true);
      try {
         const r = await fetch(
            `${API}/api/projects/${projectId}/pid/generate/${sheetNumber}`,
            { method: "POST", credentials: "include",
              headers: { "Content-Type": "application/json" }, body: "{}" });
         const d = await r.json();
         setSvg(d.svg || d.svg_content || "");
      } catch (e) {
         alert("Regenerate failed: " + e.message);
      } finally { setLoading(false); }
   }, [projectId, sheetNumber]);

   /* DOC 17 — Validate with Claude AI (3-cycle review + auto-fix). */
   const runAiValidation = useCallback(async () => {
      if (aiBusy) return;
      if (!confirm(
         "Run Claude Opus 4.6 review on this sheet?\n\n"
         + "This performs a 3-cycle review (Structure → Visual → "
         + "Compliance) with auto-fix and may take 30-90 seconds.  "
         + "Each run consumes API credits.")) {
         return;
      }
      setAiBusy(true);
      setAiReview(null);
      try {
         const r = await fetch(
            `${API}/api/projects/${projectId}/drawing/${sheetNumber}`
            + `/ai-validate?standard=ISA_ANSI`,
            { method: "POST", credentials: "include",
              headers: { "Content-Type": "application/json" }, body: "{}" });
         if (!r.ok) throw new Error(await r.text());
         const d = await r.json();
         setAiReview(d);
         if (d.svg) setSvg(d.svg);
      } catch (e) {
         alert("AI validation failed: " + e.message);
      } finally { setAiBusy(false); }
   }, [projectId, sheetNumber, aiBusy]);

   /* Natural-language AI-fix: send user prompt to Claude Opus,
    * apply returned edits, replace SVG with the new render. */
   const runAiFix = useCallback(async () => {
      const promptText = (aiPrompt || "").trim();
      if (!promptText) return;
      setAiFixBusy(true);
      setAiFixResult(null);
      try {
         const r = await fetch(
            `${API}/api/projects/${projectId}/drawing/${sheetNumber}/ai-fix`,
            {
               method: "POST",
               credentials: "include",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({ prompt: promptText }),
            });
         if (!r.ok) throw new Error(await r.text());
         const d = await r.json();
         if (d.svg) setSvg(d.svg);
         setAiFixResult({
            reasoning: d.reasoning || "",
            applied:   d.applied   || [],
            requested: d.requested || [],
         });
      } catch (e) {
         setAiFixResult({ error: String(e).slice(0, 300) });
      } finally {
         setAiFixBusy(false);
      }
   }, [projectId, sheetNumber, aiPrompt]);


   /* Tag search — highlights matching elements. */
   const runSearch = useCallback(async (q) => {
      setSearchQuery(q);
      if (!q || q.length < 2) {
         setSearchMatches([]);
         clearHighlights();
         return;
      }
      try {
         const r = await fetch(
            `${API}/api/projects/${projectId}/drawing/${sheetNumber}/search?tag=${encodeURIComponent(q)}`,
            { credentials: "include" });
         const d = await r.json();
         setSearchMatches(d.matches || []);
         applyHighlights(d.matches || []);
      } catch (e) {
         console.error("search failed:", e);
      }
   }, [projectId, sheetNumber]);

   /* Highlight matched elements in the SVG. */
   const applyHighlights = useCallback((matches) => {
      const host = svgHostRef.current;
      if (!host) return;
      clearHighlights();
      matches.forEach((m) => {
         const g = host.querySelector(`[data-elem-id="${cssEscape(m.elem_id)}"]`);
         if (g) {
            g.classList.add("pid-highlight");
            g.setAttribute("data-highlighted", "true");
         }
      });
   }, []);

   const clearHighlights = useCallback(() => {
      const host = svgHostRef.current;
      if (!host) return;
      host.querySelectorAll("[data-highlighted]").forEach((n) => {
         n.classList.remove("pid-highlight");
         n.removeAttribute("data-highlighted");
      });
   }, []);

   /* Export handlers. */
   const doExport = useCallback(async (kind) => {
      setBusyExport(kind);
      try {
         const url = kind === "bom"
            ? `${API}/api/projects/${projectId}/drawing/${sheetNumber}/bom.xlsx`
            : `${API}/api/projects/${projectId}/drawing/${sheetNumber}/drawing.dxf`;
         const r = await fetch(url, { credentials: "include" });
         if (!r.ok) throw new Error(await r.text());
         const blob = await r.blob();
         const a = document.createElement("a");
         a.href = URL.createObjectURL(blob);
         a.download = kind === "bom"
            ? `BOM_sheet_${sheetNumber}.xlsx`
            : `drawing_sheet_${sheetNumber}.dxf`;
         a.click();
         URL.revokeObjectURL(a.href);
      } catch (e) {
         alert(`${kind.toUpperCase()} export failed: ` + e.message);
      } finally { setBusyExport(null); }
   }, [projectId, sheetNumber]);

   /* ── Render ────────────────────────────────────────── */
   const props = selected ? buildFieldList(selected, override, setOverride) : null;

   return (
      <div style={{ position: "fixed", inset: 0, zIndex: 200,
                       background: COL.bg, display: "flex", flexDirection: "column" }}
             data-testid="interactive-pid-panel">
         <style>{`
            .pid-highlight { filter: drop-shadow(0 0 6px ${COL.glow})
                                         drop-shadow(0 0 12px ${COL.glow}); }
            .pid-elem:hover { filter: drop-shadow(0 0 4px ${COL.spark}); }
         `}</style>

         {/* Toolbar */}
         <div style={{ display: "flex", alignItems: "center", gap: 8,
                          padding: "10px 16px", background: COL.surface,
                          borderBottom: `1px solid ${COL.border}`,
                          ...mono, fontSize: 12 }}>
            <span style={{ fontWeight: 700, color: COL.spark, fontSize: 13 }}>
               Interactive P&amp;ID
            </span>
            <span style={{ color: COL.dim, fontSize: 11 }}>
               · sheet {sheetNumber}
            </span>
            <div style={{ flex: 1 }}/>

            {/* Search */}
            <div style={{ display: "flex", alignItems: "center",
                             background: COL.bg, border: `1px solid ${COL.border}`,
                             borderRadius: 3, padding: "0 8px" }}>
               <Search size={12} color={COL.dim}/>
               <input value={searchQuery}
                        onChange={(e) => runSearch(e.target.value)}
                        placeholder="search tag…"
                        data-testid="pid-search-input"
                        style={{ background: "transparent", color: COL.text,
                                    border: "none", outline: "none",
                                    padding: "6px 8px", fontSize: 12,
                                    ...mono, width: 180 }}/>
               {searchMatches.length > 0 && (
                  <span style={{ color: COL.glow, fontSize: 11, ...mono }}>
                     {searchMatches.length}
                  </span>
               )}
            </div>

            <button onClick={runAiValidation}
                       disabled={aiBusy}
                       data-testid="pid-ai-validate-btn"
                       title="Run Claude Opus 4.6 AI review (3-cycle)"
                       style={{ ...btn(COL, true),
                                   background:
                                     "linear-gradient(135deg, #7c3aed, #22d3ee)",
                                   borderColor: "#22d3ee" }}>
               <Sparkles size={12}/>
               <span>{aiBusy ? "reviewing…" : "Validate with Claude"}</span>
            </button>
            <button onClick={regenerate}
                       disabled={loading}
                       data-testid="pid-regenerate-btn"
                       title="Regenerate SVG with overrides applied"
                       style={btn(COL, false)}>
               <RefreshCw size={12}/>
               <span>Regenerate</span>
            </button>
            <button onClick={() => doExport("bom")}
                       disabled={busyExport === "bom"}
                       data-testid="pid-export-bom-btn"
                       style={btn(COL, false)}>
               <FileSpreadsheet size={12}/>
               <span>{busyExport === "bom" ? "…" : "BOM (xlsx)"}</span>
            </button>
            <button onClick={() => doExport("dxf")}
                       disabled={busyExport === "dxf"}
                       data-testid="pid-export-dxf-btn"
                       style={btn(COL, false)}>
               <Download size={12}/>
               <span>{busyExport === "dxf" ? "…" : "DXF"}</span>
            </button>
            <button onClick={() => setShowLayerPanel((v) => !v)}
                       data-testid="pid-toggle-layers-btn"
                       title="Toggle layer manager"
                       style={btn(COL, showLayerPanel)}>
               <span>{showLayerPanel ? "Hide layers" : "Layers"}</span>
            </button>
            {onClose && (
               <button onClick={onClose}
                          data-testid="pid-interactive-close-btn"
                          style={{ ...btn(COL, true), padding: "6px 8px" }}>
                  <X size={14}/>
               </button>
            )}
         </div>

         {/* AI Prompt bar — natural-language corrections via Claude Opus */}
         <div style={{
                background:  "#fef9e8",
                borderBottom:`1px solid #eab308`,
                padding:     "8px 12px",
                display:     "flex",
                gap:         8,
                alignItems:  "center",
              }}
              data-testid="pid-ai-prompt-bar">
            <span style={{ fontSize: 11, fontWeight: 600, color: "#713f12",
                                 letterSpacing: 0.5, textTransform: "uppercase" }}>
               Ask AI to fix
            </span>
            <input
               type="text"
               data-testid="pid-ai-prompt-input"
               placeholder='e.g. "Move CD-PUMP-001 to Zone C next to P-102A/B" or "Increase spacing between the columns"'
               value={aiPrompt}
               onChange={(e) => setAiPrompt(e.target.value)}
               onKeyDown={(e) => {
                  if (e.key === "Enter" && !aiFixBusy) runAiFix();
               }}
               disabled={aiFixBusy}
               style={{ flex: 1, padding: "6px 10px",
                            border: "1px solid #ca8a04",
                            borderRadius: 4, fontSize: 12,
                            background: "#fff" }}/>
            <button
               data-testid="pid-ai-prompt-submit"
               onClick={runAiFix}
               disabled={aiFixBusy || !aiPrompt.trim()}
               style={{ padding: "6px 14px",
                             background: aiFixBusy ? "#a78bfa" : "#7c3aed",
                             color: "#fff", border: "none", borderRadius: 4,
                             fontSize: 12, fontWeight: 600,
                             cursor: aiFixBusy ? "wait" : "pointer" }}>
               {aiFixBusy ? "Applying…" : "Fix with AI"}
            </button>
            {aiFixResult && (
               <span style={{ fontSize: 11, color: aiFixResult.error
                                       ? "#dc2626" : "#166534",
                                    fontStyle: "italic" }}
                        data-testid="pid-ai-prompt-result"
                        title={aiFixResult.reasoning || aiFixResult.error}>
                  {aiFixResult.error
                     ? `Failed: ${aiFixResult.error.slice(0, 40)}…`
                     : `${aiFixResult.applied.length} edit${aiFixResult.applied.length === 1 ? "" : "s"} applied`}
               </span>
            )}
         </div>

         {/* SVG viewport + layer panel + property side-panel */}
         <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
            <div ref={svgHostRef}
                    data-testid="pid-svg-host"
                    style={{ flex: 1, overflow: "auto",
                                background: "#f5f5f0",
                                padding: 16 }}
                    dangerouslySetInnerHTML={{ __html: svg }}/>
            {showLayerPanel && !selected && !aiReview && (
               <aside style={{ width: 320, background: "#ffffff",
                                    borderLeft: `1px solid ${COL.border}`,
                                    padding: 12, overflowY: "auto" }}
                         data-testid="pid-layer-side">
                  <PidLayerPanel svgContainerRef={svgHostRef}/>
               </aside>
            )}
            {/* DOC 17 — AI review results side panel */}
            {aiReview && !selected && (
               <aside style={{ width: 420, background: COL.surface,
                                    borderLeft: `1px solid ${COL.border}`,
                                    padding: 16, overflowY: "auto",
                                    color: COL.text, ...mono, fontSize: 12 }}
                         data-testid="pid-ai-review-panel">
                  <div style={{ display: "flex", alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 12 }}>
                     <div>
                        <div style={{ color: COL.spark, fontWeight: 700,
                                          fontSize: 13,
                                          display: "flex", alignItems: "center",
                                          gap: 6 }}>
                           <Sparkles size={14}/>
                           Claude AI Review
                        </div>
                        <div style={{ color: COL.dim, fontSize: 10 }}>
                           Cycles run: {aiReview.cycles_run || 0}
                           {" · "}Fixes applied: {aiReview.total_fixes_applied || 0}
                        </div>
                     </div>
                     <button onClick={() => setAiReview(null)}
                                data-testid="pid-ai-review-close-btn"
                                style={btnIcon(COL)}>
                        <X size={12}/>
                     </button>
                  </div>
                  {/* Grade + score badge */}
                  <div style={{ display: "flex", alignItems: "center",
                                    gap: 12, padding: "12px 14px",
                                    background: COL.bg,
                                    borderRadius: 6, marginBottom: 12,
                                    border: `1px solid ${
                                       aiReview.approved
                                          ? "#16a34a"
                                          : (aiReview.quality_score >= 70
                                               ? "#f59e0b"
                                               : "#dc2626")}` }}>
                     <div style={{ fontSize: 30, fontWeight: 900,
                                        color: aiReview.approved
                                           ? "#16a34a"
                                           : (aiReview.quality_score >= 70
                                                ? "#f59e0b"
                                                : "#dc2626"),
                                        ...mono, minWidth: 44 }}>
                        {aiReview.grade || "?"}
                     </div>
                     <div style={{ flex: 1 }}>
                        <div style={{ color: COL.text, fontSize: 20,
                                          fontWeight: 700 }}>
                           {aiReview.quality_score || 0}
                           <span style={{ color: COL.dim, fontSize: 12 }}>
                              /100
                           </span>
                        </div>
                        <div style={{ color: COL.dim, fontSize: 10 }}>
                           {aiReview.approved
                              ? "✓ Passed quality gate (≥ 85)"
                              : aiReview.quality_score >= 70
                                 ? "⚠ Below threshold — one more cycle"
                                 : "✗ Flagged for human review"}
                        </div>
                     </div>
                  </div>
                  {/* Per-cycle fix history */}
                  <div style={{ marginTop: 8 }}>
                     <div style={{ color: COL.dim, fontSize: 10,
                                       textTransform: "uppercase",
                                       letterSpacing: 1, marginBottom: 6 }}>
                        Review history
                     </div>
                     {(aiReview.review_history || []).map((r, i) => (
                        <div key={i} style={{ marginBottom: 8, padding: 8,
                                                   background: COL.bg,
                                                   borderRadius: 3,
                                                   border: `1px solid ${COL.border}` }}>
                           <div style={{ display: "flex",
                                             justifyContent: "space-between",
                                             fontSize: 11, marginBottom: 4 }}>
                              <span style={{ color: COL.text, fontWeight: 600 }}>
                                 Cycle {r.cycle} · {r.reviewer}
                              </span>
                              <span style={{ color: COL.dim }}>
                                 {r.quality_score || 0}/100
                              </span>
                           </div>
                           {(r.critical_count > 0 || r.major_count > 0
                              || r.minor_count > 0) && (
                              <div style={{ fontSize: 10, color: COL.dim,
                                                marginBottom: 4 }}>
                                 {r.critical_count > 0 && (
                                    <span style={{ color: "#dc2626",
                                                        marginRight: 8 }}>
                                       ● {r.critical_count} critical
                                    </span>
                                 )}
                                 {r.major_count > 0 && (
                                    <span style={{ color: "#f59e0b",
                                                        marginRight: 8 }}>
                                       ● {r.major_count} major
                                    </span>
                                 )}
                                 {r.minor_count > 0 && (
                                    <span style={{ color: "#94a3b8" }}>
                                       ● {r.minor_count} minor
                                    </span>
                                 )}
                              </div>
                           )}
                           {(r.fixes || []).slice(0, 5).map((f, j) => (
                              <div key={j} style={{ fontSize: 10,
                                                              color: COL.dim,
                                                              paddingLeft: 8,
                                                              borderLeft:
                                                                `2px solid ${
                                                                   f.severity === "critical"
                                                                      ? "#dc2626"
                                                                      : (f.severity === "major"
                                                                           ? "#f59e0b"
                                                                           : "#94a3b8")}`,
                                                              marginBottom: 3 }}>
                                 <b>{f.action}</b>
                                 {f.target_equipment && (
                                    <span> on {f.target_equipment}</span>
                                 )}
                                 {f.description && (
                                    <div style={{ color: COL.text }}>
                                       {f.description}
                                    </div>
                                 )}
                              </div>
                           ))}
                           {(r.fixes || []).length > 5 && (
                              <div style={{ fontSize: 10, color: COL.dim,
                                                paddingLeft: 8, marginTop: 3 }}>
                                 + {r.fixes.length - 5} more…
                              </div>
                           )}
                        </div>
                     ))}
                  </div>
               </aside>
            )}
            {selected && (
               <aside style={{ width: 380, background: COL.surface,
                                    borderLeft: `1px solid ${COL.border}`,
                                    padding: 16, overflowY: "auto",
                                    color: COL.text, ...mono, fontSize: 12 }}
                         data-testid="pid-property-panel">
                  <div style={{ display: "flex", alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 12 }}>
                     <div>
                        <div style={{ color: COL.spark, fontWeight: 700,
                                          fontSize: 13 }}>
                           {selected.tag || selected.line_number
                              || selected.elem_id}
                        </div>
                        <div style={{ color: COL.dim, fontSize: 10 }}>
                           {selected.kind}
                           {selected.type && ` · ${selected.type}`}
                           {selected.function && ` · ${selected.function}`}
                        </div>
                     </div>
                     <button onClick={() => setSelected(null)}
                                data-testid="pid-property-close-btn"
                                style={btnIcon(COL)}>
                        <X size={12}/>
                     </button>
                  </div>
                  {props}
                  <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                     <button onClick={saveOverride}
                                disabled={saving}
                                data-testid="pid-property-save-btn"
                                style={{ ...btn(COL, true), flex: 1 }}>
                        <Save size={12}/>
                        <span>{saving ? "…" : "Save Override"}</span>
                     </button>
                     <button onClick={clearOverride}
                                disabled={saving}
                                data-testid="pid-property-reset-btn"
                                style={btn(COL, false)}>
                        <RotateCcw size={12}/>
                     </button>
                  </div>
                  <div style={{ marginTop: 12, padding: 8,
                                    background: COL.bg, borderRadius: 3,
                                    fontSize: 10, color: COL.dim,
                                    lineHeight: 1.5 }}>
                     Changes are saved server-side.  Click <b>Regenerate</b>
                     &nbsp;in the toolbar to re-render this sheet with the
                     override applied.
                  </div>
               </aside>
            )}
         </div>

         {loading && (
            <div style={{ position: "absolute", inset: 0,
                             background: "rgba(2,6,23,0.6)",
                             display: "flex", alignItems: "center",
                             justifyContent: "center",
                             color: COL.spark, ...mono, fontSize: 13 }}
                    data-testid="pid-loading-overlay">
               generating…
            </div>
         )}
      </div>
   );
}


/* ── Dynamic property field renderer ────────────────────────
 *  Builds an editable form based on the element kind so users
 *  can edit EVERYTHING the enricher stamps (option 1-b).
 */
function buildFieldList(selected, override, setOverride) {
   const set = (k, v) => setOverride((o) => ({ ...o, [k]: v }));
   const setNested = (parent, k, v) => setOverride((o) => ({
      ...o, [parent]: { ...(o[parent] || {}), [k]: v },
   }));

   const kind = selected.kind;
   const rows = [];
   const F = (label, k, val, kind = "text") => rows.push(
      <FieldRow key={k} label={label} value={val ?? ""} kind={kind}
                    onChange={(v) => set(k, v)}
                    testid={`pid-prop-${k.toLowerCase()}`}/>);
   const NF = (label, parent, k, val, kind = "text") => rows.push(
      <FieldRow key={parent + k} label={label} value={val ?? ""} kind={kind}
                    onChange={(v) => setNested(parent, k, v)}
                    testid={`pid-prop-${parent}-${k}`}/>);

   if (kind === "equipment") {
      F("Tag",  "tag",  override.tag  ?? selected.tag);
      F("Type", "type", override.type ?? selected.type);
      F("X (mm)", "x", override.x ?? selected.x, "number");
      F("Y (mm)", "y", override.y ?? selected.y, "number");
      F("Width",  "width",  override.width  ?? selected.width,  "number");
      F("Height", "height", override.height ?? selected.height, "number");
      /* every svg_params key becomes editable */
      Object.entries(selected.svg_params || {}).forEach(([k, v]) => {
         const cur = (override.svg_params || {})[k] ?? v;
         NF(k, "svg_params", k, cur);
      });
      rows.push(<hr key="hr" style={{ borderColor: "#1e293b", margin: "10px 0" }}/>);
      rows.push(<div key="noz" style={{ color: "#94a3b8", fontSize: 10 }}>
         {selected.nozzles?.length || 0} nozzle{selected.nozzles?.length === 1 ? "" : "s"} (read-only)
      </div>);
   } else if (kind === "pipe") {
      F("Line Number", "line_number", override.line_number ?? selected.line_number);
      F("Service",     "service",     override.service     ?? selected.service);
      const sd = selected.stream_data || {};
      const osd = override.stream_data || {};
      NF("Line Class",   "stream_data", "line_class",    osd.line_class    ?? sd.line_class);
      NF("Size (NPS)",   "stream_data", "size_nps",      osd.size_nps      ?? sd.size_nps, "number");
      NF("T (°C)",       "stream_data", "T_C",           osd.T_C           ?? sd.T_C);
      NF("P (barg)",     "stream_data", "P_barg",        osd.P_barg        ?? sd.P_barg);
      NF("Flow (kg/h)",  "stream_data", "flow_kgh",      osd.flow_kgh      ?? sd.flow_kgh);
      NF("Insulation",   "stream_data", "insulation",    osd.insulation    ?? sd.insulation);
      NF("Material",     "stream_data", "material",      osd.material      ?? sd.material);
      NF("Notes",        "stream_data", "note",          osd.note          ?? sd.note);
   } else if (kind === "instrument") {
      F("Tag",         "tag",         override.tag         ?? selected.tag);
      F("Function",    "function",    override.function    ?? selected.function);
      F("Loop",        "loop",        override.loop        ?? selected.loop);
      F("Mount",       "mount",       override.mount       ?? selected.mount);
      F("System",      "system",      override.system      ?? selected.system);
      F("Signal / Set","signal",      override.signal      ?? selected.signal);
      F("SIL",         "sil",         override.sil         ?? selected.sil);
      F("SIF",         "sif",         override.sif         ?? selected.sif);
      F("Voting",      "voting",      override.voting      ?? selected.voting);
      F("ESD Level",   "esd_level",   override.esd_level   ?? selected.esd_level);
      F("Proof-Test",  "proof_test_yr", override.proof_test_yr ?? selected.proof_test_yr);
      F("Controls",    "controls",    override.controls    ?? selected.controls);
   } else if (kind === "inline") {
      F("Tag",      "tag",      override.tag      ?? selected.tag);
      F("Type",     "type",     override.type     ?? selected.type);
      F("Position (0..1)", "position", override.position ?? selected.position, "number");
      const params = selected.params || {};
      const opar = override.params || {};
      Object.entries(params).forEach(([k, v]) => {
         NF(k, "params", k, opar[k] ?? v);
      });
      // Common editable extras even if the enricher didn't set them
      // yet — user can add fail action / notes on any inline element.
      if (!("fail" in params)) NF("fail (FO/FC/FL)", "params", "fail",
                                                        opar.fail ?? "");
      if (!("note" in params)) NF("Note", "params", "note", opar.note ?? "");
   } else {
      rows.push(<div key="unk" style={{ color: "#94a3b8" }}>
         no editable fields for kind &quot;{kind}&quot;.
      </div>);
   }
   return rows;
}

function FieldRow({ label, value, kind, onChange, testid }) {
   return (
      <label style={{ display: "flex", flexDirection: "column",
                         gap: 4, marginBottom: 8 }}>
         <span style={{ fontSize: 10, color: "#94a3b8" }}>{label}</span>
         <input value={value ?? ""} type={kind}
                  data-testid={testid}
                  onChange={(e) => onChange(kind === "number" && e.target.value !== ""
                     ? Number(e.target.value) : e.target.value)}
                  style={{ background: "#020617", color: "#e2e8f0",
                              border: "1px solid #1e293b", borderRadius: 3,
                              padding: "5px 8px", fontSize: 12,
                              fontFamily: "'JetBrains Mono', monospace" }}/>
      </label>
   );
}

/* ── Tiny helpers ────────────────────────────────────────── */
function btn(c, primary) {
   return {
      background: primary ? c.spark : "transparent",
      color:      primary ? "#020617" : c.text,
      border:     `1px solid ${primary ? c.spark : c.border}`,
      padding: "6px 10px", borderRadius: 3, cursor: "pointer",
      fontSize: 11, fontWeight: 600,
      display: "inline-flex", alignItems: "center", gap: 4,
      fontFamily: "'JetBrains Mono', monospace",
   };
}
function btnIcon(c) {
   return {
      background: "transparent", color: c.dim, border: "none",
      padding: 4, cursor: "pointer", display: "flex",
      alignItems: "center", justifyContent: "center",
   };
}
function cssEscape(s) {
   // Escape CSS attribute-selector special chars.
   return String(s).replace(/(["\\])/g, "\\$1");
}
